import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';

import { DetailItem } from '../../inbound-add.config';
import {
  SkuAutocompleteService,
  SKUAutocompleteItem,
} from '../../services/sku-autocomplete.service';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@app/shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
  SearchFormData,
} from '@app/shared/components/search-form/search-form.component';
import { formatNumberWithCommas } from '@shared/utils/number-formatter.util';

@Component({
  selector: 'app-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    AutoCompleteModule,
    TableModule,
    ToastModule,
    TooltipModule,
    AppButtonComponent,
    DataTableComponent,
    SearchFormComponent,
  ],
  providers: [MessageService],
  templateUrl: './detail-dialog.component.html',
  styleUrl: './detail-dialog.component.css',
})
export class DetailDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() existingItems: DetailItem[] = []; // Add existing items for uniqueness validation
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<DetailItem | DetailItem[]>();

  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly skuAutocompleteService = inject(SkuAutocompleteService);

  detailForm!: FormGroup;
  loading = signal(false);

  // Autocomplete data
  skuCodeSuggestions = signal<SKUAutocompleteItem[]>([]);
  skuNameSuggestions = signal<SKUAutocompleteItem[]>([]);
  selectedSkuItem = signal<SKUAutocompleteItem | null>(null);

  // Table data
  skuItems: SKUAutocompleteItem[] = [];
  selectedSkus: SKUAutocompleteItem[] = [];
  totalRecords = 0;
  searchTerm = '';
  currentSearchParams: { skuCode: string; skuName: string } = {
    skuCode: '',
    skuName: '',
  };

  // Track already added SKUs
  alreadyAddedSkuCodes: Set<string> = new Set();
  alreadyAddedSkus: SKUAutocompleteItem[] = [];

  // Search configuration - will be initialized in ngOnInit
  searchFormat: FormFieldConfig[] = [];

  // Table columns configuration
  tableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.spu_code',
      prop: 'spuCode',
      type: 'text',
      width: 120,
    },
    {
      label: 'inbound.fields.spu_name',
      prop: 'spuName',
      type: 'text',
      width: 200,
    },
    {
      label: 'inbound.fields.sku_code',
      prop: 'skuCode',
      type: 'text',
      width: 150,
    },
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      type: 'text',
      width: 200,
    },
  ];

  ngOnInit(): void {
    this.initializeForm();
    this.initializeSearchFormat();
    this.initializeAlreadyAddedSkus();
  }

  ngOnChanges(): void {
    // Only handle changes when the dialog is visible
    if (this.visible && this.detailForm) {
      // Reinitialize already added SKUs when dialog opens
      this.initializeAlreadyAddedSkus();
      // Refresh existing SKU items with updated disable status and clear removed selections
      this.refreshSkuItemsWithDisableStatus();

      this.resetForm();
    } else if (this.visible && !this.detailForm) {
      // If form is not initialized yet, initialize it
      this.initializeForm();
      this.initializeAlreadyAddedSkus();
    }
  }

  private initializeForm(): void {
    this.detailForm = this.fb.group({
      skuCode: ['', [Validators.required, Validators.maxLength(50)]],
      skuName: ['', [Validators.required, Validators.maxLength(200)]],
      spuCode: [''],
      spuName: [''],
      plannedQty: [null, [Validators.required, Validators.min(1)]],
      batchNumber: [''],
      expiryDate: [''],
    });
  }

  private initializeSearchFormat(): void {
    this.searchFormat = [
      {
        key: 'skuCode',
        label: this.translateService.instant('inbound.search.sku_code'),
        type: 'text',
        placeholder: this.translateService.instant(
          'inbound.search.sku_code_placeholder'
        ),
      },
      {
        key: 'skuName',
        label: this.translateService.instant('inbound.search.sku_name'),
        type: 'text',
        placeholder: this.translateService.instant(
          'inbound.search.sku_name_placeholder'
        ),
      },
    ];
  }

  private initializeAlreadyAddedSkus(): void {
    // Clear previous data
    this.alreadyAddedSkuCodes.clear();
    this.alreadyAddedSkus = [];

    // Build set of already added SKU codes
    this.existingItems.forEach((item) => {
      this.alreadyAddedSkuCodes.add(item.skuCode);
    });
  }

  /**
   * Refresh SKU items with updated disable status
   */
  private refreshSkuItemsWithDisableStatus(): void {
    if (this.skuItems && this.skuItems.length > 0) {
      this.skuItems = this.skuItems.map((item: any) => ({
        ...item,
        disable: this.isSkuAlreadyAdded(item.skuCode) ? 1 : 0,
      }));
    }

    // Clear selection for items that are already added (duplicates)
    this.clearSelectionForRemovedItems();
  }

  /**
   * Clear selection for items that are already added (duplicates) or deleted from main table
   */
  private clearSelectionForRemovedItems(): void {
    if (this.selectedSkus && this.selectedSkus.length > 0) {
      const beforeCount = this.selectedSkus.length;

      // Filter out items that are already added (duplicates) or deleted from main table
      this.selectedSkus = this.selectedSkus.filter((selectedSku) => {
        const isAlreadyAdded = this.isSkuAlreadyAdded(selectedSku.skuCode);
        const isCurrentlyInExistingItems = this.existingItems.some(
          (item) => item.skuCode === selectedSku.skuCode
        );

        // Keep selection only if it's not already added AND is currently in existing items
        return !isAlreadyAdded && isCurrentlyInExistingItems;
      });
    }
  }

  private resetForm(): void {
    this.detailForm.reset();
    this.detailForm.patchValue({ plannedQty: null }); // Changed from 0 to null

    // Re-enable SPU fields when resetting
    this.detailForm.get('spuCode')?.enable();
    this.detailForm.get('spuName')?.enable();

    // Clear autocomplete data
    this.selectedSkuItem.set(null);
    this.skuCodeSuggestions.set([]);
    this.skuNameSuggestions.set([]);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  private generateId(): string {
    const randomString = Math.random().toString(36);
    return 'detail_' + Date.now() + '_' + randomString.slice(2);
  }

  /**
   * Check if SKU code is unique in the existing items list
   * @param skuCode The SKU code to check
   * @returns true if SKU code is unique, false if it already exists
   */
  private isSkuCodeUnique(skuCode: string): boolean {
    if (!skuCode || !this.existingItems || this.existingItems.length === 0) {
      return true;
    }

    // Check if any existing item has the same SKU code
    const duplicateItem = this.existingItems.find(
      (item) => item.skuCode === skuCode
    );

    return !duplicateItem;
  }

  getFieldError(fieldName: string): string {
    // Don't show errors for SPU fields since they're auto-filled
    if (fieldName === 'spuCode' || fieldName === 'spuName') {
      return '';
    }

    const control = this.detailForm.get(fieldName);
    if (control?.errors && (control.touched || control.dirty)) {
      if (control.errors['required']) {
        return this.translateService.instant(
          'inbound.messages.detail_dialog_field_required',
          {
            field: this.getFieldLabel(fieldName),
          }
        );
      }
      if (control.errors['min']) {
        return this.translateService.instant(
          'inbound.messages.detail_dialog_field_min_value',
          {
            field: this.getFieldLabel(fieldName),
            min: control.errors['min'].min,
          }
        );
      }
      if (control.errors['maxlength']) {
        return this.translateService.instant('common.validation.maxlength', {
          fieldName: this.getFieldLabel(fieldName),
          requiredLength: control.errors['maxlength'].requiredLength,
        });
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      skuCode: this.translateService.instant('inbound.fields.sku_code'),
      skuName: this.translateService.instant('inbound.fields.sku_name'),
      spuCode: this.translateService.instant('inbound.fields.spu_code'),
      spuName: this.translateService.instant('inbound.fields.spu_name'),
      plannedQty: this.translateService.instant(
        'inbound.fields.planned_quantity'
      ),
    };
    return labels[fieldName] || fieldName;
  }

  // Autocomplete methods
  searchSkuCode(event: any): void {
    const query = event.query;
    if (query && query.length >= 1) {
      this.skuAutocompleteService.searchBySkuCode(query).subscribe({
        next: (response) => {
          this.skuCodeSuggestions.set(response.pageContent || []);
        },
        error: (error) => {
          console.error('Error searching SKU codes:', error);
          this.skuCodeSuggestions.set([]);
        },
      });
    } else {
      this.skuCodeSuggestions.set([]);
    }
  }

  searchSkuName(event: any): void {
    const query = event.query;
    if (query && query.length >= 1) {
      this.skuAutocompleteService.searchBySkuName(query).subscribe({
        next: (response) => {
          this.skuNameSuggestions.set(response.pageContent || []);
        },
        error: (error) => {
          console.error('Error searching SKU names:', error);
          this.skuNameSuggestions.set([]);
        },
      });
    } else {
      this.skuNameSuggestions.set([]);
    }
  }

  onSkuCodeSelect(event: any): void {
    // Try different ways to get the selected item
    const selectedSku = event.value || event;

    if (selectedSku && typeof selectedSku === 'object' && selectedSku.skuCode) {
      this.selectedSkuItem.set(selectedSku);
      this.autoFillSkuData(selectedSku);
    }
  }

  onSkuNameSelect(event: any): void {
    // Try different ways to get the selected item
    const selectedSku = event.value || event;

    if (
      selectedSku &&
      typeof selectedSku === 'object' &&
      (selectedSku.skuName || selectedSku.materialName)
    ) {
      this.selectedSkuItem.set(selectedSku);
      this.autoFillSkuData(selectedSku);
    }
  }

  private autoFillSkuData(skuItem: SKUAutocompleteItem): void {
    const formData = {
      skuCode: skuItem.skuCode,
      skuName: skuItem.skuName || skuItem.materialName || '',
      spuCode: skuItem.spuCode,
      spuName: skuItem.spuName,
    };

    this.detailForm.patchValue(formData);

    // Disable SPU fields since they're auto-filled
    this.detailForm.get('spuCode')?.disable();
    this.detailForm.get('spuName')?.disable();
  }

  onSkuCodeClear(): void {
    this.clearSkuSelection();
  }

  onSkuNameClear(): void {
    this.clearSkuSelection();
  }

  private clearSkuSelection(): void {
    this.selectedSkuItem.set(null);
    this.detailForm.patchValue({
      skuCode: '',
      skuName: '',
      spuCode: '',
      spuName: '',
      plannedQty: null, // Reset quantity to null when clearing SKU selection
    });

    // Re-enable SPU fields
    this.detailForm.get('spuCode')?.enable();
    this.detailForm.get('spuName')?.enable();
  }

  // Format display for autocomplete suggestions
  formatSkuCodeSuggestion(sku: SKUAutocompleteItem): string {
    return `${sku.skuCode} - ${sku.skuName || sku.materialName}`;
  }

  formatSkuNameSuggestion(sku: SKUAutocompleteItem): string {
    return `${sku.skuName || sku.materialName} (${sku.skuCode})`;
  }

  // Custom option label functions for autocomplete
  getSkuCodeOptionLabel = (sku: SKUAutocompleteItem): string => {
    return sku.skuCode;
  };

  getSkuNameOptionLabel = (sku: SKUAutocompleteItem): string => {
    return sku.skuName || sku.materialName || '';
  };

  // Custom option value functions to ensure we get the full object
  getSkuCodeOptionValue = (sku: SKUAutocompleteItem): string => {
    return sku.skuCode;
  };

  getSkuNameOptionValue = (sku: SKUAutocompleteItem): string => {
    return sku.skuName || sku.materialName || '';
  };

  /**
   * Check if the current SKU code in the form is unique
   * Used for real-time validation feedback
   */
  isCurrentSkuCodeUnique(): boolean {
    const currentSkuCode = this.detailForm.get('skuCode')?.value;
    if (!currentSkuCode) {
      return true;
    }
    return this.isSkuCodeUnique(currentSkuCode);
  }

  /**
   * Get validation message for SKU code uniqueness
   */
  getSkuCodeUniquenessMessage(): string {
    if (!this.detailForm.get('skuCode')?.value) {
      return '';
    }

    if (!this.isCurrentSkuCodeUnique()) {
      return this.translateService.instant(
        'inbound.messages.detail_dialog_sku_code_exists'
      );
    }

    return '';
  }

  /**
   * Get dialog header based on mode
   */
  getDialogHeader(): string {
    return this.translateService.instant('inbound.dialog.select_skus');
  }

  /**
   * Format number with commas for display
   */
  formatQuantity(quantity: number | null | undefined): string {
    return formatNumberWithCommas(quantity);
  }

  // New table-based methods
  /**
   * Load SKU items for the table
   */
  loadSkuItems(): void {
    this.loading.set(true);

    // Determine which service method to use based on search parameters
    const { skuCode, skuName } = this.currentSearchParams;
    let searchObservable;

    if (skuCode && skuName) {
      // Both SKU code and name provided - use combined search
      searchObservable = this.skuAutocompleteService.searchBySkuCodeAndName(
        skuCode,
        skuName,
        0,
        20
      );
    } else if (skuCode) {
      // Only SKU code provided
      searchObservable = this.skuAutocompleteService.searchBySkuCode(
        skuCode,
        0,
        20
      );
    } else if (skuName) {
      // Only SKU name provided
      searchObservable = this.skuAutocompleteService.searchBySkuName(
        skuName,
        0,
        20
      );
    } else {
      // No search parameters - use general method
      searchObservable = this.skuAutocompleteService.getSkuItems(
        0,
        20,
        this.searchTerm
      );
    }

    searchObservable.subscribe({
      next: (response) => {
        this.skuItems = (response.content || response.pageContent || []).map(
          (item: any) => ({
            ...item,
            disable: this.isSkuAlreadyAdded(item.skuCode) ? 1 : 0,
          })
        );
        this.totalRecords = response.totalElements || 0;
        this.loading.set(false);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant(
            'inbound.errors.load_sku_items_failed'
          ),
        });
        this.loading.set(false);
      },
    });
  }

  /**
   * Handle lazy loading for pagination
   */
  onLazyLoad(event: any): void {
    const page = event.page || 0;
    const rows = event.rows || 20;
    this.loading.set(true);

    // Determine which service method to use based on search parameters
    const { skuCode, skuName } = this.currentSearchParams;
    let searchObservable;

    if (skuCode && skuName) {
      // Both SKU code and name provided - use combined search
      searchObservable = this.skuAutocompleteService.searchBySkuCodeAndName(
        skuCode,
        skuName,
        page,
        rows
      );
    } else if (skuCode) {
      // Only SKU code provided
      searchObservable = this.skuAutocompleteService.searchBySkuCode(
        skuCode,
        page,
        rows
      );
    } else if (skuName) {
      // Only SKU name provided
      searchObservable = this.skuAutocompleteService.searchBySkuName(
        skuName,
        page,
        rows
      );
    } else {
      // No search parameters - use general method
      searchObservable = this.skuAutocompleteService.getSkuItems(
        page,
        rows,
        this.searchTerm
      );
    }

    searchObservable.subscribe({
      next: (response) => {
        this.skuItems = (response.content || response.pageContent || []).map(
          (item: any) => ({
            ...item,
            disable: this.isSkuAlreadyAdded(item.skuCode) ? 1 : 0,
          })
        );
        this.totalRecords = response.totalElements || 0;
        this.loading.set(false);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant(
            'inbound.errors.load_sku_items_failed'
          ),
        });
        this.loading.set(false);
      },
    });
  }

  /**
   * Handle search form submission
   */
  onSearch(searchData: SearchFormData): void {
    // Extract search parameters
    const skuCode = searchData['skuCode'] || '';
    const skuName = searchData['skuName'] || '';

    // Store search parameters for use in loadSkuItems
    this.currentSearchParams = { skuCode, skuName };
  }

  /**
   * Handle search form reset
   */
  onSearchReset(): void {
    this.searchTerm = '';
    this.currentSearchParams = { skuCode: '', skuName: '' };
  }

  /**
   * Handle search term change (legacy method - keeping for backward compatibility)
   */
  onSearchChange(): void {
    // No longer loading SKU items from API
  }

  /**
   * Get selected items for adding to the main form
   */
  getSelectedItems(): DetailItem[] {
    // Filter out disabled SKUs from selection
    const validSelectedSkus = this.selectedSkus.filter(
      (sku) => (sku as any).disable !== 1
    );

    return validSelectedSkus.map((sku) => ({
      id: this.generateId(),
      skuCode: sku.skuCode,
      skuName: sku.skuName || sku.materialName,
      spuCode: sku.spuCode,
      spuName: sku.spuName,
      plannedQty: 1, // Default quantity, will be editable in main table
      batchNumber: '',
      expiryDate: '',
    }));
  }

  /**
   * Check if a SKU is already added
   */
  isSkuAlreadyAdded(skuCode: string): boolean {
    return this.alreadyAddedSkuCodes.has(skuCode);
  }

  /**
   * Get the selection state for SKUs (for data-table component)
   */
  getSkuSelectionState(skuItem: any): boolean {
    // If SKU is disabled (already added), show it as selected but disabled
    if (skuItem.disable === 1) {
      return true;
    }

    // Check if this SKU is currently in the existing items (not deleted)
    const isCurrentlyInExistingItems = this.existingItems.some(
      (item) => item.skuCode === skuItem.skuCode
    );

    // If the SKU is not in existing items (was deleted), don't show it as selected
    if (!isCurrentlyInExistingItems) {
      return false;
    }

    // Otherwise, use normal selection logic
    return this.selectedSkus.some(
      (selected) => selected.skuCode === skuItem.skuCode
    );
  }

  /**
   * Override the original onConfirm method to handle multiple items
   */
  onConfirm(): void {
    const selectedItems = this.getSelectedItems();

    if (selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'inbound.messages.select_sku_items'
        ),
      });
      return;
    }

    // Check if any of the selected items are already added (duplicates)
    const duplicateItems = selectedItems.filter((item) => {
      return this.isSkuAlreadyAdded(item.skuCode);
    });
    const validItems = selectedItems.filter((item) => {
      return !this.isSkuAlreadyAdded(item.skuCode);
    });

    // Show warning if there are duplicates but still add valid items
    if (duplicateItems.length > 0 && validItems.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'inbound.messages.some_skus_already_added'
        ),
      });
    } else if (duplicateItems.length > 0 && validItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'inbound.messages.all_skus_already_added'
        ),
      });
      return;
    }

    // Emit all valid (non-duplicate) items at once as an array
    if (validItems.length === 1) {
      // Single item - emit as single item for backward compatibility
      this.confirm.emit(validItems[0]);
    } else {
      // Multiple items - emit as array
      this.confirm.emit(validItems);
    }

    // Close the dialog after adding items
    this.onCancel();
  }
}
